import { html } from "lit";
import { customElement, property, query, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import '../tooltip/tooltip';
import '../popover/popover';
import { nothing } from 'lit-html';
import "../alert/alert";
import { BaseComponent } from "../../features/base-component/base-component";
import store from "../../store/store";
import { BulkAddToCartResponse, Product } from "../cart/shop-cart/interfaces/shop-cart.interface";
import { StoreState } from "../../store/store.interface";
import { PLACEMENT_TOP } from "../../common/constants/bs";
import emit from "../../common/events";
import { getShopCart } from "../cart/shop-cart/thunks/get-shop-cart.thunk";
import { bulkAddToCart } from "../cart/shop-cart/thunks/bulk-add-to-cart.thunk";
import { MessageType } from "../alert/global-message.interface";

/**
 * Allows the user to quickly order a comma separated list of ISBNs
 */
@customElement('mhecomm-quick-order')
export class QuickOrder extends connect(store)(BaseComponent) {
    /**
     * The title to display at the top of the component.
     */
    @property()
    title = 'Quick Order';

    /**
     * The shorter text to show the user to explain how to user the component.
     */
    @property()
    text = 'Enter ISBNs or MHIDs, separating them with commas or line breaks.';

    /**
     * The longer text to show in the popover to explain to the user how to use the component.
     */
    @property()
    details = 'Type in or copy-and-paste the PreK-12 ISBN or MHID catalogue numbers corresponding to one or more items you wish to order. If you are entering more than one ISBN or MHID, please separate each entry by a comma or a line break. After you press "Add to Cart" you will be able to edit quantities for each below. Higher Ed products are not currently supported by quick order.';

    /**
     * The label of the primary button.
     */
    @property()
    label = 'Add to Cart';

    /**
     * The label of the primary button.
     */
    @property()
    loadingLabel = 'Adding to Cart';

    /**
     * The icon to use for the details popover.
     */
    @property()
    icon = 'info-circle';

    /**
     * The number of rows to display in the text area.
     */
    @property({type: Number})
    rows = 4;

    /**
     * This is used to enable and disable the button
     * based on whether there is user input or not.
     */
    @state()
    private userInput = '';

    /**
     * This is used to tell the user how many products was added to their cart.
     */
    @state()
    private successMessage = '';

    /**
     * This is used to tell the user if any products could not be added to their cart.
     * The reason it is an array is that when the user clicks the add to cart button
     * there could be one error message with the input. Then when the response comes
     * back from the server there could be a second error message that needs displayed
     * so we don't want the second error message to suddenly replace the first error message.
     */
    @state()
    private errorMessages: string[] = [];

    /**
     * If this is set to true, the button will be disabled and will have a loading icon.
     */
    @state()
    private loading = false;

    @query('textarea')
    private textarea: HTMLTextAreaElement | undefined;

    @query('mhecomm-alert[type="danger"]')
    private errorAlertElement: HTMLTextAreaElement | undefined;

    private products: Product[] = [];

    connectedCallback() {
        super.connectedCallback();
        store.dispatch(getShopCart({}));
    }

    stateChanged(state: StoreState) {
        this.products = state.shopCartState.getShopCart.data?.products || [];
    }

    renderComp() {
        return html`
            <h2 class="mt-3">${this.title}</h2>
            <p class="d-flex">
                <span class="pe-2">${this.text}</span>
                <mhecomm-popover id="${this.id}-quick-order-popover" icon=${this.icon} text=${this.details} placement=${PLACEMENT_TOP}></mhecomm-popover>
            </p>
            ${ this.successMessage.length > 0 ? html`
                <mhecomm-alert message=${this.successMessage} type="${MessageType.success}" ?dismissible=${true}></mhecomm-alert>
            `: nothing }
            <textarea class="form-control ${this.errorMessages.length > 0 ? 'is-invalid' : ''}" rows=${this.rows} @input=${this.updateUserInput}></textarea>
            ${ this.errorMessages.length > 0 ? html`
                <p class="invalid-feedback">
                    ${this.uxtIconReady ? html`<uxt-icon icon="exclamation-triangle"></uxt-icon>` : ''}
                    ${this.errorMessages.join('. ')}
                </p>
            `: ''}
            <button
                @click=${this.addIsbns}
                class="btn btn-primary btn-lg my-3"
                ?disabled=${!this.userInput || this.loading}>
                ${ !this.loading ? html`
                    ${this.label}
                `: html`
                    ${this.loadingLabel}
                    <div class="spinner-border spinner-border-sm" role="status"></div>
                `}
            </button>
        `;
    }

    /**
     * This keeps track of the user input so that we have access to
     * it in other methods via the this.userInput internal property.
     */
    updateUserInput() {
        if (this.textarea) {
            this.userInput = this.textarea.value;
        }
    }

    /**
     * @returns This returns multiple arrays contained in an object, parsing the
     *          user input into all the validIsbns, invalidIsbns, and inCartIsbns.
     */
    parseInput() {
        const isbns = this.userInput.split(',')
            .map(item => item.split('\n')).flat()
            .map((isbn: string) => isbn.trim())
            .filter((isbn: string) => !!isbn);

        const validIsbnsPotentialDuplicates = isbns
            .filter((isbn: string) => !this.inCart(isbn))
            .filter((isbn: string) => this.validIsbn(isbn));
        
        const validIsbns: string[] = [];

        for (const isbn of validIsbnsPotentialDuplicates) {
            if (!validIsbns.find(otherIsbn => otherIsbn === isbn)) {
                validIsbns.push(isbn);
            }
        }

        const invalidIsbns = isbns.filter((isbn: string) => !this.validIsbn(isbn));

        const inCartIsbns = isbns.filter((isbn: string) => this.inCart(isbn));

        return { validIsbns, invalidIsbns, inCartIsbns };
    }

    updated() {
        if (this.errorAlertElement) {
            this.errorAlertElement.addEventListener('alert-close', () => this.errorMessages = []);
        }
    }

    /**
     * Add the isbns that are listed in the textarea into cart.
     * This splits the textarea value by commas and newlines.
     * It also performs some checks to validate that the isbn actually needs added.
     */
    addIsbns() {
        const { validIsbns, invalidIsbns, inCartIsbns } = this.parseInput();
        this.successMessage = '';
        this.emitQuickOrderAdd(validIsbns);

        if (validIsbns.length > 0) {
            this.errorMessages = invalidIsbns.length !== 0
                ? [`The following ${ invalidIsbns.length > 1 ? 'isbns are' : 'isbn is' } invalid: ${invalidIsbns.join(', ')}`]
                : [];
            this.dispatchQuickOrderAdd(validIsbns).finally(() => {
                if (this.textarea) {
                    if (this.errorMessages.length === 0) {
                        this.textarea.value = '';
                        this.userInput = '';
                    } else {
                        this.textarea.value = invalidIsbns.join(',');
                    }
                }
            });
        } else {
            this.errorMessages = inCartIsbns.length > 0
                ? ['All of the isbns are already in your cart. Please add isbns that are not already in your cart.']
                : ['No valid isbns found in input. Please add valid isbns in a comma separated list.'];
        }
    }

    /**
     * This dispatches the bulk add to cart request and updates success and errror
     * message as neccessary.
     * 
     * @param isbns The isbns to add to cart.
     */
    private async dispatchQuickOrderAdd(isbns: string[]): Promise<any> {
        this.loading = true;
        return store.dispatch(bulkAddToCart({isbns: isbns, addItemsToMineCart: false}))
        .then(res => {
            this.loading = false;
            const response = res.payload as BulkAddToCartResponse[];
            const successfulIsbns: BulkAddToCartResponse[] = [];
            const productEquals = (p1: BulkAddToCartResponse, p2: BulkAddToCartResponse) => {
                if (p1.productIdentifier.isbn10 && p2.productIdentifier.isbn10) {
                    return p1.productIdentifier.isbn10 === p2.productIdentifier.isbn10;
                } else if (p1.productIdentifier.isbn13 && p2.productIdentifier.isbn13) {
                    return p1.productIdentifier.isbn13 === p2.productIdentifier.isbn13;
                } else {
                    return false;
                }
            };
            for (const product of response) {
                if (!product.errorResponse && !successfulIsbns.find(p => productEquals(p, product))) {
                    successfulIsbns.push(product);
                }
            }
            if (successfulIsbns.length > 0) {
                this.successMessage = `Adding ${successfulIsbns.length} ${successfulIsbns.length > 1 ? 'products' : 'product'} to cart.`;
            } else {
                // At this point, there might already be an error message displayed.
                // So push a new error message instead of resetting the whole array.
                this.errorMessages.push(`Could not add ${isbns.length > 1 ? 'isbns' : 'isbn'} to cart: ${isbns.join(', ')}. Please try again.`);
            }
        }).catch(() => {
            this.loading = false;
            // At this point, there might already be an error message displayed.
            // So push a new error message instead of resetting the whole array.
            this.errorMessages.push('Error adding products to cart')
        });
    }

    /**
     * Emits a 'quick-order-add' event.
     * 
     * @param isbns The isbns getting added.
     */
    private emitQuickOrderAdd(isbns: string[]) {
        emit({
            element: this,
            name: 'quick-order-add',
            detail: {
                isbns: isbns,
            }
        });
    }

    /**
     * This is used to make sure we do not request to add isbns that are already in the cart.
     * 
     * @param isbn The isbn to check
     * @returns True if this isbn is not already in the cart.
     */
    private inCart(isbn: string) {
        return !! this.products.find(product => [ product.productIdentifier.isbn10, product.productIdentifier.isbn13, product.productIdentifier.mhid ].includes(isbn));
    }

    /**
     * Valid isbns are 10 or 13 characters long and are all numbers
     * except for the last character which might be a letter.
     * 
     * @param isbn The string to validate.
     * @returns Whether the provided string is a valid isbn.
     */
    private validIsbn(isbn: string) {
        return (isbn.length === 10 || isbn.length === 13)
               && /^\d*[a-zA-Z]?$/.test(isbn);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'mhecomm-quick-order': QuickOrder;
    }
}
